import * as React from 'react';
import styled from 'react-emotion';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import history from "../../utils/history";
import "bulma/css/bulma.css";
import './style.css';
import {Button, Control, Field, Icon, Input, Label} from "bloomer";
import {getUserData, saveUserData, uploadProfilePicture} from "../../utils/auth";
import Spacer from "../../elements/Spacer";
import ChangePasswordForm from "./ChangePasswordForm";
import ProfilePictureCrop from "./ProfilePictureCrop";

const Container = styled("div")`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: 20px minmax(80vh, auto) minmax(19vh, auto);
  min-height: 100vh;

  @media (max-width: 1024px) {
    grid-template-rows: 20px auto;
  }
`;

const ContentOuter = styled("div")`
  margin-top: 70px;
  grid-column: 3/11;
  @media (max-width: 1024px) {
    grid-column: 2/12;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    grid-column: 1/-1;
  }
`;

const Content = styled("div")`
  display: flex;
  grid-column: 3/11;
  @media (max-width: 1024px) {
    grid-column: 2/12;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    grid-column: 1/-1;
  }
`;

const Heading = styled("span")`
  font-size: 24px;
  margin-left: 0px;
  margin-top: 30px;
`;

const BackButtonContainer = styled("div")`
    text-align: left;
    color: #2f3241;
    margin-bottom: 20px;
    margin-left: 10px;
    width: 100%;
`;

const AccountContent = styled("div")`
    flex: 1;
    text-align: left;
    margin: 0 20px;
    color: #2f3241;
`;

const ProfilePictureContent = styled("div")`
    width: 300px;
    text-align: left;
    margin: 0 20px;
    color: #2f3241;
`;

const SelectImageButtonContainer = styled("div")`
    display: flex;
    text-align: left;
`;

const ButtonsContainer = styled("div")`
    display: flex;
    text-align: left;
    margin-left: 20px;
`;

const InputWarning = styled("span")`
  text-align: left;
  line-height: 30px;
  vertical-align: middle;
  font-size: 14px;
  color: red;
`;

const SuccessMessage = styled("div")`
  text-align: left;
  line-height: 30px;
  vertical-align: middle;
  font-size: 14px;
  color: green;
`;

const ErrorMessage = styled("span")`
  text-align: left;
  line-height: 30px;
  vertical-align: middle;
  font-size: 14px;
  color: red;
`;

const StyledImage = styled('img')`
  position: absolute;
  margin: auto;
  padding: 0;
  border: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  max-width: 298px;
  max-height: 298px;
`;

interface ISettingsProps {
    onUpdateUserContext: any;
    openContactForm: any;
}

interface ISettingsState {
    savingUserData: boolean;
    imageUploading: boolean;
    email: string;
    firstName: string;
    firstNameInvalid: boolean;
    firstNameErrorMessage: string;
    lastName: string;
    lastNameInvalid: boolean;
    lastNameErrorMessage: string;
    profilePictureUrl: any;
    profilePictureImage: any;
    enableSaveButton: boolean;
    showChangePasswordForm: boolean;
    showProfilePictureEditForm: boolean;
    saveSuccessMessage: string;
    saveErrorMessage: string;
    passwordChangedSuccessMessage: string;
}

class Settings extends React.Component<ISettingsProps, ISettingsState> {
    public NAME_REGEX = new RegExp('^[a-zA-Z\\s]+$');
    public NAME_MAX_LENGTH = 30;

    public constructor(props: ISettingsProps) {
        super(props);

        this.state = {
            savingUserData: false,
            imageUploading: false,
            email: "",
            firstName: "",
            firstNameInvalid: false,
            firstNameErrorMessage: "",
            lastName: "",
            lastNameInvalid: false,
            lastNameErrorMessage: "",
            profilePictureUrl: null,
            profilePictureImage: null,
            enableSaveButton: false,
            showChangePasswordForm: false,
            showProfilePictureEditForm: false,
            saveSuccessMessage: "",
            saveErrorMessage: "",
            passwordChangedSuccessMessage: ""
        };
    }

    public componentDidMount() {
        this.loadData();
    }

    public handleFirstNameChange = (e: any) => {
        this.setState({
            firstName: e.currentTarget.value,
            firstNameInvalid: false,
            firstNameErrorMessage: "",
            enableSaveButton: true,
            saveSuccessMessage: "",
            saveErrorMessage: ""
        }, () => {
            const errorMessage = this.validateNameField(this.state.firstName.trim());
            if (errorMessage) {
                this.setState({
                    firstNameInvalid: true,
                    firstNameErrorMessage: errorMessage
                });
            }
        });
    };

    public handleLastNameChange = (e: any) => {
        this.setState({
            lastName: e.currentTarget.value,
            lastNameInvalid: false,
            lastNameErrorMessage: "",
            enableSaveButton: true,
            saveSuccessMessage: "",
            saveErrorMessage: ""
        }, () => {
            const errorMessage = this.validateNameField(this.state.lastName.trim());
            if (errorMessage) {
                this.setState({
                    lastNameInvalid: true,
                    lastNameErrorMessage: errorMessage
                });
            }
        });
    };

    public validateNameField = (value: any) => {
        if (!value) {
            return "This field is required.";
        } else if (value.length > this.NAME_MAX_LENGTH) {
            return "Maximum number of characters exceeded.";
        } else if (!this.NAME_REGEX.test(value)) {
            return "Use English letters.";
        }
        return "";
    };

    public loadData = async () => {
        const userData = await getUserData();
        if (userData) {
            this.setState({
                email: userData.email,
                firstName: userData.first_name || "",
                lastName: userData.last_name || "",
                profilePictureUrl: userData.profile_picture_url || ""
            });

            this.props.onUpdateUserContext(userData);
        }
    };

    public onSave = async (e: any) => {
        e.target.blur();

        if (this.state.savingUserData) {
            return;
        }

        let valid = true;

        const firstName = this.state.firstName.trim();
        const firstNameFieldErrorMessage = this.validateNameField(firstName);
        if (firstNameFieldErrorMessage) {
            this.setState({
                firstNameInvalid: true,
                firstNameErrorMessage: firstNameFieldErrorMessage
            });
            valid = false;
        }

        const lastName = this.state.lastName.trim();
        const lastNameFieldErrorMessage = this.validateNameField(lastName);
        if (lastNameFieldErrorMessage) {
            this.setState({
                lastNameInvalid: true,
                lastNameErrorMessage: lastNameFieldErrorMessage
            });
            valid = false;
        }

        if (!valid) {
            return;
        }

        this.setState({
            savingUserData: true,
            saveSuccessMessage: "",
            saveErrorMessage: ""
        })

        const response = await saveUserData(firstName, lastName);
        if (response) {
            this.loadData();
            this.setState({
                savingUserData: false,
                enableSaveButton: false,
                saveSuccessMessage: "User profile updated successfully."
            });
        } else {
            this.setState({
                savingUserData: false,
                saveErrorMessage: "Couldn't save user info."
            });
        }
    };

    public render() {
        // TODO remove inline styles
        return (
            <Container>
                <Header history={history}/>
                <ContentOuter>
                    <BackButtonContainer>
                        <Button
                            style={{
                                border: 0,
                                color: "#1c2541"
                            }}
                            title="Go To Dashboard"
                            onClick={(e: any) => {
                                e.target.blur();
                                history.push("/models");
                            }}
                        >
                            <Icon className="fas fa-arrow-left" style={{marginRight: "5px"}}/>Go Back
                        </Button>
                    </BackButtonContainer>
                    <Content>
                        <AccountContent>
                            <Heading>Account Settings</Heading>
                            <hr/>
                            <Field>
                                <Label>Email</Label>
                                <Control>
                                    <Input
                                        disabled={true}
                                        type="text"
                                        placeholder="Email"
                                        value={this.state.email}
                                    />
                                </Control>
                            </Field>

                            <Field>
                                <Label>First Name</Label>
                                <Control>
                                    <Input
                                        type="text"
                                        placeholder="First Name"
                                        value={this.state.firstName}
                                        onChange={this.handleFirstNameChange}
                                    />
                                </Control>
                                {
                                    this.state.firstNameInvalid ?
                                        <InputWarning>{this.state.firstNameErrorMessage}</InputWarning> : <></>
                                }
                            </Field>
                            <Field>
                                <Label>Last Name</Label>
                                <Control>
                                    <Input
                                        type="text"
                                        placeholder="Last Name"
                                        value={this.state.lastName}
                                        onChange={this.handleLastNameChange}
                                    />
                                </Control>
                                {
                                    this.state.lastNameInvalid ?
                                        <InputWarning>{this.state.lastNameErrorMessage}</InputWarning> : <></>
                                }
                            </Field>
                            <Spacer/>

                            <Button className="has-background-light" style={{
                                marginRight: '20px',
                                width: '240px'
                            }}
                                    onClick={(e: any) => {
                                        e.target.blur();

                                        this.setState({
                                            showChangePasswordForm: true,
                                            passwordChangedSuccessMessage: ""
                                        });
                                    }}
                            >CHANGE PASSWORD</Button>
                            {
                                this.state.passwordChangedSuccessMessage ? <SuccessMessage>
                                    <Icon
                                        className="fas fa-check"
                                        style={{margin: '5px'}}
                                    />{this.state.passwordChangedSuccessMessage}
                                </SuccessMessage> : <></>
                            }
                            <Spacer/>
                            {
                                this.state.showChangePasswordForm ? <ChangePasswordForm
                                    onClose={(passwordChanged?: boolean) => {
                                        this.setState({showChangePasswordForm: false});
                                        if (passwordChanged) {
                                            this.setState({
                                                passwordChangedSuccessMessage: "Password changed successfully."
                                            });
                                        }
                                    }}
                                /> : <></>
                            }
                            {
                                this.state.showProfilePictureEditForm ? <ProfilePictureCrop
                                    image={this.state.profilePictureImage}
                                    onClose={() => {
                                        this.setState({
                                            showProfilePictureEditForm: false
                                        });
                                    }}
                                    onSave={async (blob: any) => {
                                        const formData = new FormData();
                                        formData.append("profile_picture", blob);
                                        this.setState({
                                            showProfilePictureEditForm: false,
                                            imageUploading: true,
                                            profilePictureUrl: null
                                        });
                                        const ok = await uploadProfilePicture(formData);
                                        if (ok) {
                                            await this.loadData();
                                        }
                                        this.setState({
                                            imageUploading: false
                                        });
                                    }}
                                /> : <></>
                            }
                        </AccountContent>
                        <ProfilePictureContent>
                            <Heading>Profile Picture</Heading>
                            <hr/>
                            <div style={{
                                position: 'relative',
                                textAlign: 'center',
                                border: '1px solid #dbdbdb',
                                color: '#dbdbdb',
                                height: '300px'
                            }}>
                                {
                                    this.state.imageUploading ? <div style={{
                                        marginTop: '142px',
                                        color: 'grey'
                                    }}>
                                        <span>Uploading...</span>
                                        <Icon className="fas fa-spinner fa-spin"/>
                                    </div> : (this.state.profilePictureUrl ? <div>
                                            <StyledImage
                                                src={this.state.profilePictureUrl}
                                            />
                                        </div> :
                                        <Icon
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                fontSize: '200px'
                                            }}
                                            className="fas fa-user"
                                        />)
                                }
                            </div>
                            <Spacer/>
                            <SelectImageButtonContainer>
                                <Button className="has-background-light" style={{
                                    margin: '0 20px',
                                    width: '100%'
                                }}
                                        onClick={(e: any) => {
                                            e.target.blur();

                                            if (this.state.imageUploading) {
                                                return;
                                            }

                                            const input = document.createElement("input");
                                            input.type = "file";
                                            input.accept = ".png,.jpg,.jpeg,.svg";
                                            input.addEventListener('change', async () => {
                                                const file = input.files[0];
                                                if (file) {
                                                    const extension = file.name.split('.').reverse()[0];
                                                    if (['png', 'jpg', 'jpeg', 'svg'].indexOf(extension) === -1) {
                                                        return;
                                                    }

                                                    const reader = new FileReader();
                                                    reader.onload = () => {
                                                        this.setState({
                                                            profilePictureImage: reader.result,
                                                            showProfilePictureEditForm: true
                                                        });
                                                    };
                                                    reader.readAsDataURL(file);


                                                }
                                                // input.value = '';
                                            });
                                            input.style.display = 'none';
                                            input.click();
                                        }}
                                >Select Image</Button>
                            </SelectImageButtonContainer>
                        </ProfilePictureContent>
                    </Content>
                    <div>
                        <hr/>
                        <Spacer/>
                        {
                            this.state.savingUserData ? <div>
                                <span>Saving...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div> : <></>
                        }
                        {
                            !this.state.savingUserData ? (!this.state.saveErrorMessage && !this.state.saveSuccessMessage ?
                                <ButtonsContainer>
                                    <Button
                                        onClick={this.onSave}
                                        disabled={!this.state.enableSaveButton}
                                        className="has-text-white has-background-grey" style={{
                                        marginRight: '20px',
                                        width: '150px'
                                    }}>Save</Button>
                                    <Button
                                        disabled={!this.state.enableSaveButton}
                                        className="has-background-light"
                                        style={{
                                            width: '150px'
                                        }}
                                        onClick={(e: any) => {
                                            e.target.blur();

                                            this.loadData();
                                            this.setState({
                                                savingUserData: false,
                                                firstNameInvalid: false,
                                                firstNameErrorMessage: "",
                                                lastNameInvalid: false,
                                                lastNameErrorMessage: "",
                                                profilePictureUrl: null,
                                                enableSaveButton: false,
                                                showChangePasswordForm: false,
                                                saveSuccessMessage: "",
                                                saveErrorMessage: ""
                                            });
                                        }}
                                    >Cancel</Button>
                                </ButtonsContainer> : <div>
                                    {
                                        this.state.saveSuccessMessage ? <SuccessMessage>
                                            <Icon
                                                className="fas fa-check"
                                                style={{margin: '0 5px'}}
                                            />{this.state.saveSuccessMessage}
                                        </SuccessMessage> : <></>
                                    }
                                    {
                                        this.state.saveErrorMessage ?
                                            <ErrorMessage>{this.state.saveErrorMessage}</ErrorMessage> : <></>
                                    }
                                </div>) : <></>
                        }
                    </div>
                    <Spacer/>
                    <Spacer/>
                    <Spacer/>
                </ContentOuter>
                <Footer openContactForm={this.props.openContactForm} />
            </Container>
        );
    }
}

export default Settings;


// WEBPACK FOOTER //
// ./src/routes/Settings/index.tsx